import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';
import { User, LoginResponse, TenantInfo } from '../types';
import { checkForUpdates } from '../serviceWorkerRegistration';

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
  /** Tenant actual (multi-tenant). Null si login legacy. */
  tenantId: number | null;
  tenantName: string | null;
  tenantCode: string | null;
  /** Lista de tenants del usuario (para selector "Cambiar empresa"). */
  tenants: TenantInfo[];
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<void>;
  /** Cambia el tenant activo y devuelve un nuevo JWT. */
  switchTenant: (tenantId: number) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const TENANT_ID_KEY = 'tenantId';
const TENANT_NAME_KEY = 'tenantName';
const TENANT_CODE_KEY = 'tenantCode';
const TENANTS_LIST_KEY = 'tenants';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('token'));
  const [isAuthenticated, setIsAuthenticated] = useState(() => !!localStorage.getItem('token'));
  const [user, setUser] = useState<User | null>(null);
  const [tenantId, setTenantId] = useState<number | null>(() => {
    const v = localStorage.getItem(TENANT_ID_KEY);
    return v ? parseInt(v, 10) : null;
  });
  const [tenantName, setTenantName] = useState<string | null>(() => localStorage.getItem(TENANT_NAME_KEY));
  const [tenantCode, setTenantCode] = useState<string | null>(() => localStorage.getItem(TENANT_CODE_KEY));
  const [tenants, setTenants] = useState<TenantInfo[]>(() => {
    try {
      const raw = localStorage.getItem(TENANTS_LIST_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      setToken(storedToken);
      api.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
      setIsAuthenticated(true);
      const storedUsername =
        localStorage.getItem('username') ||
        localStorage.getItem('userName') ||
        localStorage.getItem('user') ||
        '';
      const storedRolesRaw = localStorage.getItem('roles');
      let storedRoles: string[] = [];
      try {
        storedRoles = storedRolesRaw ? JSON.parse(storedRolesRaw) : [];
      } catch {
        storedRoles = [];
      }
      if (storedUsername) {
        setUser({ username: storedUsername, roles: storedRoles } as User);
      }
      const tid = localStorage.getItem(TENANT_ID_KEY);
      setTenantId(tid ? parseInt(tid, 10) : null);
      setTenantName(localStorage.getItem(TENANT_NAME_KEY));
      setTenantCode(localStorage.getItem(TENANT_CODE_KEY));
      try {
        const tlist = localStorage.getItem(TENANTS_LIST_KEY);
        setTenants(tlist ? JSON.parse(tlist) : []);
      } catch {
        setTenants([]);
      }
      checkForUpdates();
    } else {
      setIsAuthenticated(false);
    }
  }, []);

  const login = async (username: string, password: string) => {
    try {
      const response = await api.post<LoginResponse>('/api/auth/login', { username, password });
      const data = response.data;
      const { token: newToken, refreshToken, username: userUsername, roles } = data;

      localStorage.setItem('token', newToken);
      localStorage.setItem('refreshToken', refreshToken);
      // Persistir identidad básica para rehidratación post-refresh
      localStorage.setItem('username', userUsername);
      localStorage.setItem('userName', userUsername);
      localStorage.setItem('user', userUsername);
      localStorage.setItem('roles', JSON.stringify(roles || []));

      // Persistir info de tenant si viene de MasterDB
      if (data.tenantId != null) {
        localStorage.setItem(TENANT_ID_KEY, String(data.tenantId));
        localStorage.setItem(TENANT_NAME_KEY, data.tenantName ?? '');
        localStorage.setItem(TENANT_CODE_KEY, data.tenantCode ?? '');
        setTenantId(data.tenantId);
        setTenantName(data.tenantName ?? null);
        setTenantCode(data.tenantCode ?? null);
      } else {
        localStorage.removeItem(TENANT_ID_KEY);
        localStorage.removeItem(TENANT_NAME_KEY);
        localStorage.removeItem(TENANT_CODE_KEY);
        setTenantId(null);
        setTenantName(null);
        setTenantCode(null);
      }
      if (data.tenants?.length) {
        localStorage.setItem(TENANTS_LIST_KEY, JSON.stringify(data.tenants));
        setTenants(data.tenants);
      } else {
        localStorage.removeItem(TENANTS_LIST_KEY);
        setTenants([]);
      }

      api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;

      setUser({ username: userUsername, roles } as User);
      setToken(newToken);
      setIsAuthenticated(true);

      // Verificar actualizaciones del service worker después del login
      checkForUpdates();
    } catch (error) {
      console.error('Error al iniciar sesión:', error);
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('grupoCajaChicaSeleccionado');
    localStorage.removeItem('grupoCajaMcp');
    localStorage.removeItem('username');
    localStorage.removeItem('userName');
    localStorage.removeItem('user');
    localStorage.removeItem('roles');
    localStorage.removeItem(TENANT_ID_KEY);
    localStorage.removeItem(TENANT_NAME_KEY);
    localStorage.removeItem(TENANT_CODE_KEY);
    localStorage.removeItem(TENANTS_LIST_KEY);
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
    setToken(null);
    setTenantId(null);
    setTenantName(null);
    setTenantCode(null);
    setTenants([]);
    setIsAuthenticated(false);
  };

  const refreshToken = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        logout();
        return;
      }

const response = await api.post<LoginResponse>('/api/auth/RefreshToken', { refreshToken });
        const data = response.data;
        const newToken = data.token;
        const newRefreshToken = data.refreshToken;

        if (!newToken || !newRefreshToken) {
          throw new Error('Token o refreshToken no recibidos');
        }

        localStorage.setItem('token', newToken);
        localStorage.setItem('refreshToken', newRefreshToken);
        if (data.tenantId != null) {
          localStorage.setItem(TENANT_ID_KEY, String(data.tenantId));
          localStorage.setItem(TENANT_NAME_KEY, data.tenantName ?? '');
          localStorage.setItem(TENANT_CODE_KEY, data.tenantCode ?? '');
          setTenantId(data.tenantId);
          setTenantName(data.tenantName ?? null);
          setTenantCode(data.tenantCode ?? null);
        }

        api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        setToken(newToken);
    } catch (error) {
      console.error('Error al actualizar el token:', error);
      logout();
    }
  };

  const switchTenant = async (newTenantId: number) => {
    try {
      const response = await api.post<LoginResponse>('/api/auth/switchtenant', { tenantId: newTenantId });
      const data = response.data;
      localStorage.setItem('token', data.token);
      localStorage.setItem('refreshToken', data.refreshToken);
      if (data.tenantId != null) {
        localStorage.setItem(TENANT_ID_KEY, String(data.tenantId));
        localStorage.setItem(TENANT_NAME_KEY, data.tenantName ?? '');
        localStorage.setItem(TENANT_CODE_KEY, data.tenantCode ?? '');
        setTenantId(data.tenantId);
        setTenantName(data.tenantName ?? null);
        setTenantCode(data.tenantCode ?? null);
      }
      api.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
      setToken(data.token);
      window.location.reload();
    } catch (error) {
      console.error('Error al cambiar tenant:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      user,
      token,
      tenantId,
      tenantName,
      tenantCode,
      tenants,
      login,
      logout,
      refreshToken,
      switchTenant
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
}; 